import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Annotation, Transaction } from '@codemirror/state';
import { ViewUpdate } from '@codemirror/view';
import { CodeLang } from '@common/codemirror/types/code-lang.type';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabDocSerializationError } from '@common/collab/types/collab-doc-serialization-error.type';
import { DocSizeError } from '@common/html/constants/doc-size-error.constant';
import { nodeToJSON } from '@common/prosemirror/json/prosemirror-json';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { createTransactionForState } from '@common/prosemirror/recreate-transform/recreate';
import { CollabSession, CollabSessionTransactionTooLargeEvent, CollabSessionUpdateEvent } from '@common/sockets/collab-session';
import { CollabSessionSyncState } from '@common/sockets/enums/collab-session-sync-state.enum';
import { getSchemaByParams } from '@common/util/schema-set';
import { environment } from '@env/environment';
import { ApiService } from '@portal-core/auth/services/api.service';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { CodeEditorComponent } from '@portal-core/code-editor/components/code-editor/code-editor.component';
import { CodeEditorService } from '@portal-core/code-editor/services/code-editor.service';
import { CodeChangesRecreatorResult, CodeChangesRecreatorRunMessage } from '@portal-core/code-editor/types/code-changes-recreator.type';
import { DocStepsRecreatorResult, DocStepsRecreatorRunMessage } from '@portal-core/code-editor/types/doc-steps-recreator.type';
import { DataService } from '@portal-core/data/common/services/data.service';
import { FileSizeService } from '@portal-core/general/services/file-size.service';
import { OnlineService } from '@portal-core/general/services/online.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { TaskWorkerBase } from '@portal-core/util/task-worker.base';
import { debounce } from 'lodash';
import { Step } from 'prosemirror-transform';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, Observable, Subscription, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

const codeEditorCollabEditAnnotation = Annotation.define<boolean>();

/**
 * A component for editing code files (that use a prosemirror schema) collaboratively.
 * This component is a wrapper around the CodeEditorComponent.
 * It creates a CollabSession for the CodeEditorComponent and manages the syncing of the collab session doc and the code editor.
 */
@Component({
  selector: 'mc-collab-code-editor',
  templateUrl: './collab-code-editor.component.html',
  styleUrls: ['./collab-code-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class CollabCodeEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() branchName: string;
  /** Whether the collab session can receive document updates. For example, it can be paused when the component is inactive or hidden. */
  @Input() canReceiveUpdates: boolean = true;
  @Input() codeChangesRecreatorTaskWorker: TaskWorkerBase<CodeChangesRecreatorResult>;
  @Input() commitId: string;
  @Input() docStepsRecreatorTaskWorker: TaskWorkerBase<DocStepsRecreatorResult>;
  @Input() filePath: string;
  @Input() fileType: CollabFileType;
  @Input() lang?: CodeLang;
  @Input() licenseUser: LicenseUser;
  @Input() projectId: number;
  @Input() projectFileType: ProjectFileType;
  @Input() readonly?: boolean = false;
  @Input() reviewFileId: number;
  @Input() wrapLines?: boolean;

  @Output() contentChange: EventEmitter<void> = new EventEmitter<void>();
  @Output() load: EventEmitter<void> = new EventEmitter<void>();
  @Output() unload: EventEmitter<void> = new EventEmitter<void>();
  @Output() wrapLinesChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @InputObservable(['branchName', 'commitId', 'filePath', 'fileType', 'projectId', 'reviewFileId']) collabSessionInputs$: Observable<[string, string, string, CollabFileType, number, number, CollabSchema]>;
  @InputObservable('canReceiveUpdates') canReceiveUpdates$: Observable<boolean>;
  @InputObservable('codeChangesRecreatorTaskWorker') codeChangesRecreatorTaskWorker$: Observable<TaskWorkerBase<CodeChangesRecreatorResult>>;
  @InputObservable('docStepsRecreatorTaskWorker') docStepsRecreatorTaskWorker$: Observable<TaskWorkerBase<DocStepsRecreatorResult>>;
  @InputObservable('licenseUser') private licenseUser$: Observable<LicenseUser>;
  @InputObservable('readonly') readonlyInput$: Observable<boolean>;

  @ViewChild(CodeEditorComponent, { static: true }) codeEditor: CodeEditorComponent;

  public get docVersion(): number {
    return this.collabSession?.docVersion;
  }

  get lineCount(): number {
    return this.codeEditor.editorState?.doc.lines;
  }

  /** The subscription for canReceiveUpdates changes. */
  private canReceiveUpdatesSubscription: Subscription;
  /** The subscription for the code changes recreator result. */
  private codeChangesRecreatorSubscription: Subscription;
  /** The prosemirror collab session that the code editor is synced with.  */
  collabSession: CollabSession;
  /** The subscription for the creating the collab session. */
  private collabSessionSubscription: Subscription;
  /** The subscription for the doc steps recreator result. */
  private docStepsRecreatorSubscription: Subscription;
  /** The subscription for the online status. */
  private onlineSubscription: Subscription;
  /** Whether or not the editor should be readonly. */
  readonly$: Observable<boolean>;
  /** An observable to trigger reloads. */
  private reload$: BehaviorSubject<void> = new BehaviorSubject<void>(null);
  /** The schema for the editors. */
  private schema: CollabSchema;
  /** The error from the most recent attempt to sync the code editor to the collab session but the doc was too large to serialize. */
  tooLargeError: CollabDocSerializationError;
  /** The error from the most recent attempt to sync the code editor to the collab session (excluding doc too larger errors). */
  validationError: Error;
  /** A debounced function for syncing the code editor content to the collab session. Syncing is expensive so throttle how often it runs. */
  private debouncedSyncCodeEditorToCollabSession: CollabCodeEditorComponent['syncCodeEditorToCollabSession'] = debounce(() => {
    this.syncCodeEditorToCollabSession();
  }, 1000);

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private dataService: DataService,
    private fileSizeService: FileSizeService,
    private codeEditorService: CodeEditorService,
    private ngZone: NgZone,
    private onlineService: OnlineService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    // Subscribe to the code changes recreator in order to sync the collab session to the code editor
    this.codeChangesRecreatorSubscription = this.codeChangesRecreatorTaskWorker$.pipe(
      filter(codeChangesRecreatorTaskWorker => !!codeChangesRecreatorTaskWorker),
      switchMap(codeChangesRecreatorTaskWorker => codeChangesRecreatorTaskWorker.complete$)
    ).subscribe(result => {
      if (result.changes?.length > 0) {
        // The results from recreateChanges return document positions that refer to each previous change in the results.
        // For codemirror to apply the changes correctly make each change its own transaction spec.
        // Add specify that the changes are sequential.
        const tr = this.codeEditor.editorState.update(...result.changes.map(change => {
          return {
            changes: [change],
            annotations: [
              Transaction.addToHistory.of(false),
              codeEditorCollabEditAnnotation.of(true)
            ],
            sequential: true
          };
        }));

        // Only apply the transaction if the doc changed
        if (tr.docChanged) {
          this.codeEditor.editorView.dispatch(tr);
        }
      }
    });

    // Subscribe to the doc steps recreator in order to sync the code editor to the collab session
    this.docStepsRecreatorSubscription = this.docStepsRecreatorTaskWorker$.pipe(
      filter(docStepsRecreatorTaskWorker => !!docStepsRecreatorTaskWorker),
      switchMap(docStepsRecreatorTaskWorker => docStepsRecreatorTaskWorker.complete$)
    ).subscribe(result => {
      if (result.error?.name === DocSizeError) {
        this.tooLargeError = result.error;
        this.validationError = null;
      } else {
        this.tooLargeError = null;
        this.validationError = result.error;

        if (this.validationError) {
          if (this.validationError.message && !this.validationError.message.endsWith('.')) {
            this.validationError.message += '. ';
          }
          this.validationError.message += 'The document will not be saved until the error is fixed.';
        }
      }

      // Mark for check to update the UI for the error changes
      this.cdr.markForCheck();

      // Because recreating the steps is asynchronous the collab session may have been destroyed before the syncing completes. So only sync if the collab session exists.
      if (this.collabSession) {
        if (result.steps?.length > 0) {
          const tr = createTransactionForState(this.collabSession.editorState, result.steps.map(step => Step.fromJSON(this.schema, step)));

          if (tr.docChanged) {
            tr.setMeta('collab-code-editor', true);
            this.collabSession.dispatch(tr);
          }
        }
        // Update whether the collab session can receive updates, this may cause fetching updates if needed.
        this.collabSession.canReceiveUpdates = this.sessionCanReceiveUpdates();
      }
    });

    // Keep the collab session up to date on the online status of the user
    this.onlineSubscription = this.onlineService.online$.subscribe(online => {
      if (this.collabSession) {
        this.collabSession.offline = !online;
      }
    });

    // Update the canReceiveUpdates property of the collab session
    this.canReceiveUpdatesSubscription = this.canReceiveUpdates$.pipe(
      distinctUntilChanged(),
    ).subscribe(() => {
      if (this.collabSession) {
        this.collabSession.canReceiveUpdates = this.sessionCanReceiveUpdates();
      }
    });
  }

  /**
   * Makes an observable subscription for creating and destroying the collaborative session based on the component's Inputs.
   * This is done in ngAfterViewInit instead of ngOnInit because mc-text-editor is ready to be used here but not in ngOnInit.
   */
  ngAfterViewInit() {
    // Subscribe in ngAfterViewInit because mc-text-editor is now ready to be used
    this.collabSessionSubscription = combineLatest([
      // We only care about a license user change if the license user has changed to a different license user
      this.licenseUser$.pipe(
        filter(licenseUser => !!licenseUser),
        distinctUntilChanged(this.dataService.sameIdentity)
      ),
      this.collabSessionInputs$,
      this.reload$
    ]).subscribe(([licenseUser, [branchName, commitId, filePath, fileType, projectId, reviewFileId]]) => {
      // Only review files have a file id
      const hasValidId = fileType === CollabFileType.Review ? Number.isInteger(reviewFileId) : true;

      // Get the schema for the file
      this.schema = getSchemaByParams(this.reviewFileId, this.filePath);

      // If all the necessary properties exist then create a collab session
      if (licenseUser && branchName && commitId && hasValidId && filePath && fileType && Number.isInteger(projectId) && this.schema) {
        this.createCollabSession();
      } else { // Else destroy a collab session if it exist
        this.destroyCollabSession();
      }
    });
  }

  /** Cleans up the component by destroying the collaborative session and stopping the workers. */
  ngOnDestroy() {
    this.destroyCollabSession();
    this.codeChangesRecreatorTaskWorker?.destroy();
    this.docStepsRecreatorTaskWorker?.destroy();
  }

  onCodeContentChanged(viewUpdate: ViewUpdate) {
    this.contentChange.emit();

    // Do not sync changes made by others through the collab session
    if (!viewUpdate.transactions.some(tr => tr.annotation(codeEditorCollabEditAnnotation))) {
      this.debouncedSyncCodeEditorToCollabSession();
    }
  }

  onRevertDocToSavedState() {
    if (this.tooLargeError) {
      this.createCollabSession();
    } else {
      this.collabSession.revertDocToSavedState();
    }
  }

  onWrapLinesChanged(wrapLines: boolean) {
    this.wrapLines = wrapLines;
    this.wrapLinesChange.emit(wrapLines);
  }

  /**
   * Forces an existing collaborative session to be destroyed and a new one created.
   * A new session is created only if the required Inputs are provided.
   */
  reload() {
    this.reload$.next(null);
  }

  /** Starts the asynchronous process of validating the code editor content and applying it to the collab session. */
  private syncCodeEditorToCollabSession() {
    // Start syncing the code editor to the collab session
    this.docStepsRecreatorTaskWorker?.run<DocStepsRecreatorRunMessage>({
      type: 'run',
      fileType: this.projectFileType,
      fromDoc: nodeToJSON(this.collabSession.editorState.doc),
      toCode: this.codeEditor.getContent()
    });
  }

  /**
   * Shows a snackbar error message when a transaction is too large.
   * @param event  The event for the transaction being too large.
   */
  private showTransactionTooLargeError(event: CollabSessionTransactionTooLargeEvent) {
    this.snackBar.open(`Changes ignored because they exceed ${this.fileSizeService.format(event.maxSize, 0)} limit.`, 'OK');
  }

  /**
   * Stops a collaborative session if it exists.
   * Cleans up all resources and unsubscribes from all events associated with the session.
   */
  private destroyCollabSession() {
    // Clear the code editor
    this.codeEditor.reset();

    // Stop the workers because they were used with the collab session
    this.codeChangesRecreatorTaskWorker?.stop();
    this.docStepsRecreatorTaskWorker?.stop();

    if (this.collabSession) {
      this.collabSession.stop();
      this.collabSession = null;
    }

    this.unload.emit();
  }

  /**
   * Creates a new connection to the collaborative server.
   * If an existing session exists it will be destroyed first.
   */
  private createCollabSession() {
    // Stop an existing collab session
    this.destroyCollabSession();

    // Reset the errors
    this.tooLargeError = null;
    this.validationError = null;

    // Create a new collab session
    this.collabSession = new CollabSession({
      socketBaseUrl: this.apiService.collabAuthorityUrl,
      offline: !this.onlineService.online,
      refreshToken$: () => this.authService.refreshToken$(),
      branchName: this.branchName,
      canReceiveUpdates: this.sessionCanReceiveUpdates(),
      clientId: uuidv4(),
      reviewFileId: this.reviewFileId,
      filePath: this.filePath,
      fileType: this.fileType,
      projectId: this.projectId,
      schema: this.schema,
      socketMaxHttpBufferSizeBytes: environment.collabSocketMaxHttpBufferSizeBytes ? parseInt(environment.collabSocketMaxHttpBufferSizeBytes, 10) : undefined,
      userDataEnabled: false, // The collab session in the content editor typically will handle the user data so disable it in the code editor
      userId: this.licenseUser?.User?.Id ?? null,
      userName: this.licenseUser?.User?.FullName ?? null
    });

    this.collabSession.on('load', () => this.load.emit());

    this.collabSession.on('update', (event: CollabSessionUpdateEvent) => {
      // If this is the initial doc load (no transaction) or the doc changed from outside the code editor
      if (!event.transaction || (event.transaction.docChanged && !event.transaction.getMeta('collab-code-editor'))) {
        // Only sync the collab changes to the code editor if the code is valid
        if (!this.validationError) {
          // Start syncing the collab session to the code editor
          this.codeChangesRecreatorTaskWorker?.run<CodeChangesRecreatorRunMessage>({
            type: 'run',
            schemaName: this.schema.name,
            fromCode: this.codeEditor.getContent(),
            toDoc: nodeToJSON(event.session.editorState.doc)
          });
        }
      }
    });

    this.collabSession.on('transactionTooLarge', (event: CollabSessionTransactionTooLargeEvent) => {
      this.ngZone.run(() => {
        this.showTransactionTooLargeError(event);
      });
    });

    // Put the editor into readonly mode if the collab session is in a state where it can't send edits
    this.readonly$ = combineLatest([
      this.collabSession.connected$,
      this.collabSession.exceedsMongoDBMaxSize$,
      this.collabSession.loaded$,
      this.collabSession.offline$,
      this.collabSession.readOnly$,
      this.collabSession.stopped$,
      this.collabSession.syncState$,
      this.readonlyInput$
    ]).pipe(
      map(([connected, exceedsMongoDBMaxSize, loaded, offline, readOnly, stopped, syncState, readonlyInput]) => {
        return !connected || exceedsMongoDBMaxSize || !loaded || offline || readOnly || stopped || syncState === CollabSessionSyncState.Reverting || readonlyInput;
      })
    );

    // Start the collab session
    this.collabSession.start();
  }

  /**
   * Determines if the collab session can receive updates based on the component's state.
   * @returns {boolean} True if the collab session can receive updates, false otherwise.
   */
  private sessionCanReceiveUpdates(): boolean {
    // The collab session can receive updates if this is allowed and there are no validation errors
    return this.canReceiveUpdates && !this.validationError && !this.tooLargeError;
  }
}
